import type React from "react";

// Arimo has Helvetica's character widths, so lines break where the PDF's do. The
// generic family stands in, visibly, should a font fail to load.
const ARIMO = "Arimo, Arial, sans-serif";
const STANDARD_FONTS: Record<string, React.CSSProperties> = {
  Helvetica: { fontFamily: ARIMO, fontWeight: 400, fontStyle: "normal" },
  "Helvetica-Bold": { fontFamily: ARIMO, fontWeight: 700, fontStyle: "normal" },
  "Helvetica-Oblique": {
    fontFamily: ARIMO,
    fontWeight: 400,
    fontStyle: "italic"
  },
  "Helvetica-BoldOblique": {
    fontFamily: ARIMO,
    fontWeight: 700,
    fontStyle: "italic"
  }
};

const HELVETICA_FACES = [
  "Helvetica",
  "Helvetica-Bold",
  "Helvetica-Oblique",
  "Helvetica-BoldOblique"
];
const FIGTREE_FACES = [
  "Figtree",
  "Figtree-Light",
  "Figtree-Medium",
  "Figtree-SemiBold"
];

// The face bold and italic text is drawn in, as the PDF picks it: Helvetica's four, Figtree's
// SemiBold for bold (it ships no italic, so italic stays upright), any other font as it is.
export const faceOf = (font: string, bold: boolean, italic: boolean) => {
  if (HELVETICA_FACES.includes(font)) {
    const isBold = font.includes("Bold") || bold;
    const isItalic = font.includes("Oblique") || italic;
    if (isBold && isItalic) return "Helvetica-BoldOblique";
    if (isBold) return "Helvetica-Bold";
    return isItalic ? "Helvetica-Oblique" : "Helvetica";
  }
  return FIGTREE_FACES.includes(font) && bold ? "Figtree-SemiBold" : font;
};

// A template's own font is registered under the name its `fonts` map gives it.
export const fontCss = (font: string): React.CSSProperties =>
  STANDARD_FONTS[font] ?? {
    fontFamily: `'${font}', sans-serif`,
    fontWeight: 400,
    fontStyle: "normal"
  };

let context: CanvasRenderingContext2D | null | undefined;

// A line's natural width in points, unkerned like the PDF; 0 where there is no canvas.
export const textWidth = (font: string, size: number, text: string) => {
  if (context === undefined) {
    context =
      typeof document === "undefined"
        ? null
        : document.createElement("canvas").getContext("2d");
    if (context) context.fontKerning = "none";
  }
  if (!context) return 0;
  const { fontFamily, fontWeight, fontStyle } = fontCss(font);
  context.font = `${fontStyle} ${fontWeight} ${size}px ${fontFamily}`;
  return context.measureText(text).width;
};

// Measured with whatever font was in use, so a font registering later clears it.
const baselines = new Map<string, number | undefined>();

// Where the browser puts the baseline in a line box of this height; undefined without layout.
export const browserBaseline = (
  font: string,
  size: number,
  lineHeight: number
) => {
  const key = `${font}|${size}|${lineHeight}`;
  if (!baselines.has(key)) {
    let baseline: number | undefined;
    if (typeof document !== "undefined") {
      const box = document.createElement("div");
      Object.assign(box.style, fontCss(font), {
        position: "absolute",
        left: "-9999px",
        whiteSpace: "nowrap",
        fontSize: `${size}px`,
        lineHeight: `${lineHeight}px`
      });
      const probe = document.createElement("span");
      probe.style.display = "inline-block";
      box.append("H", probe);
      document.body.append(box);
      const measured =
        probe.getBoundingClientRect().bottom - box.getBoundingClientRect().top;
      box.remove();
      baseline = measured > 0 ? measured : undefined;
    }
    baselines.set(key, baseline);
  }
  return baselines.get(key);
};

const ARIMO_STYLESHEET =
  "https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400;0,700;1,400;1,700&display=block";
const ARIMO_FACES = [
  "400 10px Arimo",
  "700 10px Arimo",
  "italic 400 10px Arimo",
  "italic 700 10px Arimo"
];

// A load that failed isn't kept, so the next document tries again.
let arimo: Promise<boolean> | undefined;
const registered = new Map<string, Promise<boolean>>();

const canLoadFonts = () =>
  typeof document !== "undefined" && "fonts" in document;

const loadArimo = () => {
  if (!arimo) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = ARIMO_STYLESHEET;
    document.head.appendChild(link);
    arimo = new Promise<boolean>((resolve) => {
      link.onload = () => resolve(true);
      link.onerror = () => resolve(false);
    })
      .then((declared) =>
        declared
          ? Promise.all(ARIMO_FACES.map((face) => document.fonts.load(face)))
          : []
      )
      // Each face must have matched one that loaded; a check of the set alone
      // also passes when the stylesheet declared none.
      .then(
        (faces) =>
          faces.length > 0 && faces.every((matched) => matched.length > 0),
        () => false
      )
      .then((loaded) => {
        if (loaded) baselines.clear();
        else {
          link.remove();
          arimo = undefined;
        }
        return loaded;
      });
  }
  return arimo;
};

// Loaded from the file's bytes rather than its URL: the app's content security
// policy allows fonts only from its own and a few named origins.
const registerFont = (name: string, url: string) => {
  const key = `${name}|${url}`;
  let loading = registered.get(key);
  if (!loading) {
    loading = fetch(url)
      .then((response) => response.arrayBuffer())
      .then((data) => new FontFace(name, data).load())
      .then(
        (face) => {
          document.fonts.add(face);
          baselines.clear();
          return true;
        },
        () => {
          registered.delete(key);
          return false;
        }
      );
    registered.set(key, loading);
  }
  return loading;
};

// Resolves once every font that can load has, with the names of any that couldn't:
// laying out before then would use a fallback font's widths.
export const loadFonts = (
  templateFonts: Record<string, string> = {}
): Promise<string[]> => {
  if (!canLoadFonts()) return Promise.resolve([]);
  const names = ["Arimo", ...Object.keys(templateFonts)];
  return Promise.all([
    loadArimo(),
    ...Object.entries(templateFonts).map(([name, url]) =>
      registerFont(name, url)
    )
  ]).then((loaded) => names.filter((_, index) => !loaded[index]));
};
